import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  CheckCircle,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LockOpen as UnlockIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Group as GroupIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useTranslation } from 'react-i18next';

interface Permission {
  id: string;
  codigo: string;
  nome: string;
  descricao: string;
  modulo: string;
  accao: string;
  recurso: string;
  estado: string;
  dataRegisto: string;
  perfisCount: number;
}

interface Profile {
  id: string;
  codigo: string;
  descricao: string;
  estado: string;
  dataRegisto: string;
  users?: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Permissions: React.FC = () => {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [moduloFilter, setModuloFilter] = useState('all');
  const [accaoFilter, setAccaoFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [saving, setSaving] = useState(false);

  // Profile permissions state
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [profilePermissions, setProfilePermissions] = useState<string[]>([]);
  const [permissionsByModule, setPermissionsByModule] = useState<Record<string, Permission[]>>({});
  const [savingProfile, setSavingProfile] = useState(false);

  // Profile CRUD state
  const [profileDialog, setProfileDialog] = useState(false);
  const [selectedProfileEdit, setSelectedProfileEdit] = useState<Profile | null>(null);
  const [profileFormData, setProfileFormData] = useState({ codigo: '', descricao: '', estado: 'A' });
  const [savingProfileEdit, setSavingProfileEdit] = useState(false);
  const [profileDetailDialog, setProfileDetailDialog] = useState(false);
  const [profileDetail, setProfileDetail] = useState<any>(null);
  const [loadingProfileDetail, setLoadingProfileDetail] = useState(false);

  // Lists
  const [modulos, setModulos] = useState<{ modulo: string; count: number }[]>([]);
  const [accoes, setAccoes] = useState<{ accao: string; count: number }[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    modulo: '',
    accao: '',
    recurso: '',
    estado: 'A',
  });

  const getStatusChip = (status: string) => {
    return status === 'A' ? (
      <Chip size="small" color="success" label={t('common.active')} icon={<CheckCircle />} />
    ) : (
      <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />
    );
  };

  const getAccaoChip = (accao: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
      VIEW: 'info',
      CREATE: 'success',
      EDIT: 'warning',
      DELETE: 'error',
      MANAGE: 'secondary',
      FULL: 'primary',
    };
    return <Chip size="small" color={colors[accao] || 'default'} label={accao} />;
  };

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/permissions', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          modulo: moduloFilter !== 'all' ? moduloFilter : undefined,
          accao: accaoFilter !== 'all' ? accaoFilter : undefined,
        },
      });

      if (response.data.success) {
        setPermissions(response.data.data || []);
        setModulos(response.data.modulos || []);
        setAccoes(response.data.accoes || []);
        if (response.data.needsMigration) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching permissions:', err);
      if (err.response?.status === 404) {
        setNeedsSetup(true);
      } else {
        setError(t('permissions.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, moduloFilter, accaoFilter]);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await api.get('/admin/profiles');
      if (response.data.success) {
        const raw: Profile[] = response.data.data || [];
        const seen = new Set<string>();
        setProfiles(raw.filter(p => { if (seen.has(p.codigo)) return false; seen.add(p.codigo); return true; }));
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  }, []);

  const fetchProfilePermissions = useCallback(async (profileId: string) => {
    if (!profileId) return;
    try {
      const response = await api.get(`/admin/permissions/profile/${profileId}`);
      if (response.data.success) {
        const permIds = response.data.data.map((p: any) => p.id);
        setProfilePermissions(permIds);
      }
    } catch (err) {
      console.error('Error fetching profile permissions:', err);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
    fetchProfiles();
  }, [fetchPermissions, fetchProfiles]);

  useEffect(() => {
    // Group permissions by module for the profile assignment tab
    const grouped: Record<string, Permission[]> = {};
    permissions.forEach(perm => {
      if (!grouped[perm.modulo]) {
        grouped[perm.modulo] = [];
      }
      grouped[perm.modulo].push(perm);
    });
    setPermissionsByModule(grouped);
  }, [permissions]);

  useEffect(() => {
    if (selectedProfile) {
      fetchProfilePermissions(selectedProfile);
    } else {
      setProfilePermissions([]);
    }
  }, [selectedProfile, fetchProfilePermissions]);

  const handleSetupTables = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await api.post('/admin/permissions/migrate');
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setNeedsSetup(false);
        fetchPermissions();
      }
    } catch (err: any) {
      setError(t('permissions.errorSettingUp'));
    } finally {
      setSettingUp(false);
    }
  };

  const handleOpenEdit = (permission?: Permission) => {
    if (permission) {
      setSelectedPermission(permission);
      setFormData({
        codigo: permission.codigo,
        nome: permission.nome,
        descricao: permission.descricao || '',
        modulo: permission.modulo,
        accao: permission.accao,
        recurso: permission.recurso || '',
        estado: permission.estado,
      });
    } else {
      setSelectedPermission(null);
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        modulo: '',
        accao: 'VIEW',
        recurso: '',
        estado: 'A',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedPermission) {
        await api.put(`/admin/permissions/${selectedPermission.id}`, formData);
        setSuccessMessage(t('permissions.permissionUpdated'));
      } else {
        await api.post('/admin/permissions', formData);
        setSuccessMessage(t('permissions.permissionCreated'));
      }
      setEditDialog(false);
      setSelectedPermission(null);
      fetchPermissions();
    } catch (err: any) {
      setError(err.response?.data?.error || t('permissions.errorSavingPermission'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (permission: Permission) => {
    if (permission.perfisCount > 0) {
      setError(t('permissions.cannotDelete', { count: permission.perfisCount }));
      return;
    }
    if (window.confirm(t('permissions.confirmDeletePermission', { name: permission.nome }))) {
      try {
        await api.delete(`/admin/permissions/${permission.id}`);
        setSuccessMessage(t('permissions.permissionDeleted'));
        fetchPermissions();
      } catch (err: any) {
        setError(err.response?.data?.error || t('permissions.errorDeletingPermission'));
      }
    }
  };

  const handleTogglePermission = (permId: string) => {
    setProfilePermissions(prev =>
      prev.includes(permId)
        ? prev.filter(id => id !== permId)
        : [...prev, permId]
    );
  };

  const handleToggleModule = (modulo: string, checked: boolean) => {
    const modulePermIds = permissionsByModule[modulo]?.map(p => p.id) || [];
    setProfilePermissions(prev => {
      if (checked) {
        return Array.from(new Set([...prev, ...modulePermIds]));
      } else {
        return prev.filter(id => !modulePermIds.includes(id));
      }
    });
  };

  const handleSaveProfilePermissions = async () => {
    if (!selectedProfile) return;
    setSavingProfile(true);
    try {
      await api.put(`/admin/permissions/profile/${selectedProfile}/sync`, {
        permissaoIds: profilePermissions,
      });
      setSuccessMessage(t('permissions.profilePermissionsUpdated'));
    } catch (err: any) {
      setError(err.response?.data?.error || t('permissions.errorSavingProfile'));
    } finally {
      setSavingProfile(false);
    }
  };

  // Profile CRUD handlers
  const handleOpenProfileCreate = () => {
    setSelectedProfileEdit(null);
    setProfileFormData({ codigo: '', descricao: '', estado: 'A' });
    setProfileDialog(true);
  };

  const handleOpenProfileEdit = (profile: Profile) => {
    setSelectedProfileEdit(profile);
    setProfileFormData({ codigo: profile.codigo, descricao: profile.descricao, estado: profile.estado });
    setProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfileEdit(true);
    try {
      if (selectedProfileEdit) {
        await api.put(`/admin/profiles/${selectedProfileEdit.id}`, {
          descricao: profileFormData.descricao,
          estado: profileFormData.estado,
        });
        setSuccessMessage(t('permissions.profileUpdated'));
      } else {
        await api.post('/admin/profiles', profileFormData);
        setSuccessMessage(t('permissions.profileCreated'));
      }
      setProfileDialog(false);
      fetchProfiles();
    } catch (err: any) {
      setError(err.response?.data?.error || t('permissions.errorSavingProfileCrud'));
    } finally {
      setSavingProfileEdit(false);
    }
  };

  const handleDeleteProfile = async (profile: Profile) => {
    if (!window.confirm(t('permissions.confirmDeleteProfile', { description: profile.descricao, code: profile.codigo }))) return;
    try {
      await api.delete(`/admin/profiles/${profile.id}`);
      setSuccessMessage(t('permissions.profileDeleted'));
      fetchProfiles();
    } catch (err: any) {
      setError(err.response?.data?.error || t('permissions.errorDeletingProfile'));
    }
  };

  const handleViewProfile = async (profile: Profile) => {
    setProfileDetailDialog(true);
    setLoadingProfileDetail(true);
    try {
      const response = await api.get(`/admin/profiles/${profile.id}`);
      if (response.data.success) {
        setProfileDetail(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching profile detail:', err);
    } finally {
      setLoadingProfileDetail(false);
    }
  };

  const isModuleFullySelected = (modulo: string) => {
    const modulePermIds = permissionsByModule[modulo]?.map(p => p.id) || [];
    return modulePermIds.length > 0 && modulePermIds.every(id => profilePermissions.includes(id));
  };

  const isModulePartiallySelected = (modulo: string) => {
    const modulePermIds = permissionsByModule[modulo]?.map(p => p.id) || [];
    const selected = modulePermIds.filter(id => profilePermissions.includes(id));
    return selected.length > 0 && selected.length < modulePermIds.length;
  };

  const stats = {
    total: permissions.length,
    active: permissions.filter(p => p.estado === 'A').length,
    modules: new Set(permissions.map(p => p.modulo)).size,
  };

  const filteredPermissions = permissions;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SecurityIcon color="primary" fontSize="large" />
          <Typography variant="h5" fontWeight={600}>
            {t('permissions.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPermissions}>
            {t('common.refresh')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={seeding}
            startIcon={seeding ? <CircularProgress size={16} /> : <SettingsIcon />}
            onClick={async () => {
              setSeeding(true);
              setError(null);
              try {
                const r = await api.post('/admin/permissions/seed');
                setSuccessMessage(r.data?.message || 'Seed concluído');
                fetchPermissions();
              } catch (e: any) {
                setError(e.response?.data?.error || 'Erro ao correr seed');
              } finally {
                setSeeding(false);
              }
            }}
          >
            Seed
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('permissions.newPermission')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {!loading && needsSetup && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTables}
              disabled={settingUp}
            >
              {settingUp ? t('permissions.settingUp') : t('permissions.setupTables')}
            </Button>
          }
        >
          {t('permissions.setupNeeded')}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab icon={<GroupIcon />} iconPosition="start" label={t('permissions.tabProfiles')} />
          <Tab icon={<SecurityIcon />} iconPosition="start" label={t('permissions.tabPermissions')} />
          <Tab icon={<PersonIcon />} iconPosition="start" label={t('permissions.tabAssign')} />
        </Tabs>
      </Paper>

      {/* Tab 0: Perfis */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenProfileCreate}>
            {t('permissions.newProfile')}
          </Button>
        </Box>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={120}>{t('common.code')}</TableCell>
                  <TableCell>{t('common.description')}</TableCell>
                  <TableCell width={100}>{t('common.status')}</TableCell>
                  <TableCell width={130} align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">{t('permissions.noProfiles')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  profiles.map((profile) => (
                    <TableRow key={profile.id} hover>
                      <TableCell>
                        <Chip label={profile.codigo} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{profile.descricao}</Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(profile.estado)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleViewProfile(profile)} title={t('common.details')}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenProfileEdit(profile)} title={t('common.edit')}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteProfile(profile)} title={t('common.delete')}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 4 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon color="primary" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('permissions.totalPermissions')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('permissions.activeCount')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon color="info" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.modules}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('permissions.modules')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('permissions.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t('permissions.module')}
                  value={moduloFilter}
                  onChange={(e) => setModuloFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('permissions.allModules')}</MenuItem>
                  {modulos.map((m) => (
                    <MenuItem key={m.modulo} value={m.modulo}>
                      {m.modulo} ({m.count})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t('permissions.action')}
                  value={accaoFilter}
                  onChange={(e) => setAccaoFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('permissions.allActions')}</MenuItem>
                  {accoes.map((a) => (
                    <MenuItem key={a.accao} value={a.accao}>
                      {a.accao} ({a.count})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Table */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={120}>{t('common.code')}</TableCell>
                      <TableCell>{t('common.name')}</TableCell>
                      <TableCell width={120}>{t('permissions.module')}</TableCell>
                      <TableCell width={100}>{t('permissions.action')}</TableCell>
                      <TableCell width={100}>{t('permissions.resource')}</TableCell>
                      <TableCell width={80} align="center">{t('permissions.profilesCount')}</TableCell>
                      <TableCell width={100}>{t('common.status')}</TableCell>
                      <TableCell width={100} align="center">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPermissions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">{t('permissions.noPermissions')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPermissions.map((perm) => (
                        <TableRow key={perm.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.75rem">
                              {perm.codigo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{perm.nome}</Typography>
                            <Typography variant="caption" color="text.secondary">{perm.descricao}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={perm.modulo} variant="outlined" />
                          </TableCell>
                          <TableCell>{getAccaoChip(perm.accao)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                              {perm.recurso || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={perm.perfisCount} color={perm.perfisCount > 0 ? 'primary' : 'default'} />
                          </TableCell>
                          <TableCell>{getStatusChip(perm.estado)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenEdit(perm)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(perm)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredPermissions.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={t('common.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
              />
            </>
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Profile Permissions Assignment */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label={t('permissions.selectProfile')}
                  value={selectedProfile}
                  onChange={(e) => setSelectedProfile(e.target.value)}
                >
                  <MenuItem value="">{t('permissions.selectProfilePlaceholder')}</MenuItem>
                  {profiles.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.codigo} - {p.descricao}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {selectedProfile && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {profilePermissions.length} {t('permissions.permissionsSelected')}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={savingProfile ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveProfilePermissions}
                      disabled={savingProfile}
                    >
                      {t('permissions.savePermissions')}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {selectedProfile ? (
          <Box>
            {Object.entries(permissionsByModule).map(([modulo, perms]) => (
              <Accordion key={modulo} defaultExpanded={perms.some(p => profilePermissions.includes(p.id))}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Checkbox
                      checked={isModuleFullySelected(modulo)}
                      indeterminate={isModulePartiallySelected(modulo)}
                      onChange={(e) => handleToggleModule(modulo, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Typography fontWeight={600}>{modulo}</Typography>
                    <Chip
                      size="small"
                      label={`${perms.filter(p => profilePermissions.includes(p.id)).length}/${perms.length}`}
                      color={isModuleFullySelected(modulo) ? 'success' : isModulePartiallySelected(modulo) ? 'warning' : 'default'}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    {perms.map((perm) => (
                      <ListItem key={perm.id} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={profilePermissions.includes(perm.id)}
                            onChange={() => handleTogglePermission(perm.id)}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={perm.nome}
                          secondary={perm.descricao}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {getAccaoChip(perm.accao)}
                            {profilePermissions.includes(perm.id) ? (
                              <UnlockIcon color="success" fontSize="small" />
                            ) : (
                              <LockIcon color="disabled" fontSize="small" />
                            )}
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {t('permissions.selectProfileMsg')}
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Profile Create/Edit Dialog */}
      <Dialog open={profileDialog} onClose={() => setProfileDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          {selectedProfileEdit ? t('permissions.editProfile') : t('permissions.newProfile')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={profileFormData.codigo}
                onChange={(e) => setProfileFormData({ ...profileFormData, codigo: e.target.value.toUpperCase() })}
                disabled={!!selectedProfileEdit}
                placeholder={t('permissions.codePlaceholder')}
                helperText={t('permissions.codeHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={profileFormData.estado}
                onChange={(e) => setProfileFormData({ ...profileFormData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={profileFormData.descricao}
                onChange={(e) => setProfileFormData({ ...profileFormData, descricao: e.target.value })}
                placeholder={t('permissions.profileDescriptionPlaceholder')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={!profileFormData.codigo || !profileFormData.descricao || savingProfileEdit}
            startIcon={savingProfileEdit ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingProfileEdit ? t('common.saving') : selectedProfileEdit ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Detail Dialog */}
      <Dialog open={profileDetailDialog} onClose={() => setProfileDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          {t('permissions.profileDetails')}
          {profileDetail && (
            <Chip label={profileDetail.codigo} size="small" variant="outlined" sx={{ fontFamily: 'monospace', ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingProfileDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : profileDetail ? (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.code')}</Typography>
                  <Typography variant="body1" fontFamily="monospace">{profileDetail.codigo}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(profileDetail.estado)}</Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.description')}</Typography>
                  <Typography variant="body1">{profileDetail.descricao}</Typography>
                </Grid>
              </Grid>

              {profileDetail.users && profileDetail.users.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('permissions.usersWithProfile')} ({profileDetail.users.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('common.username')}</TableCell>
                          <TableCell>{t('common.email')}</TableCell>
                          <TableCell>{t('common.name')}</TableCell>
                          <TableCell width={80}>{t('common.status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {profileDetail.users.map((u: any) => (
                          <TableRow key={u.id}>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{u.username}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{u.email || '-'}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{u.nome || '-'}</Typography></TableCell>
                            <TableCell>{getStatusChip(u.estado)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {profileDetail.users && profileDetail.users.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {t('permissions.noUsersWithProfile')}
                </Typography>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDetailDialog(false)}>{t('common.close')}</Button>
          {profileDetail && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => {
              setProfileDetailDialog(false);
              handleOpenProfileEdit(profileDetail);
            }}>
              {t('common.edit')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Permission Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon />
          {selectedPermission ? t('permissions.editPermission') : t('permissions.newPermission')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                disabled={!!selectedPermission}
                placeholder="Ex: USERS_VIEW"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder={t('permissions.permissionNamePlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('common.description')}
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('permissions.module')}
                value={formData.modulo}
                onChange={(e) => setFormData({ ...formData, modulo: e.target.value.toUpperCase() })}
                placeholder="Ex: GESTAO"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('permissions.action')}
                value={formData.accao}
                onChange={(e) => setFormData({ ...formData, accao: e.target.value })}
              >
                <MenuItem value="VIEW">VIEW</MenuItem>
                <MenuItem value="CREATE">CREATE</MenuItem>
                <MenuItem value="EDIT">EDIT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
                <MenuItem value="MANAGE">MANAGE</MenuItem>
                <MenuItem value="EXPORT">EXPORT</MenuItem>
                <MenuItem value="SEND">SEND</MenuItem>
                <MenuItem value="REFUND">REFUND</MenuItem>
                <MenuItem value="CANCEL">CANCEL</MenuItem>
                <MenuItem value="FULL">FULL</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('permissions.resource')}
                value={formData.recurso}
                onChange={(e) => setFormData({ ...formData, recurso: e.target.value.toLowerCase() })}
                placeholder="Ex: users"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.codigo || !formData.nome || !formData.modulo || !formData.accao || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? t('common.saving') : selectedPermission ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions;
